/**
 * The store's clock, in the admin (T18).
 *
 * "Today" on the Bills list and the dashboard is the store's day: the API
 * buckets bills by the location's own timezone, else the tenant's, else IST —
 * and an all-locations view by the tenant's. These helpers pick the same zone
 * from `/auth/me` (tenant.timezone, locations[].timezone) so the dates the admin
 * sends and the times it shows agree with the server, whatever zone this
 * browser runs in.
 */
import { DEFAULT_TIMEZONE, isSupportedTimezone } from '@bbpos/types';
import type { DateRange } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import type { UserProfile } from '@/types/auth';

/** The zone of `locationId` (its own, else the business's); no store = the business's. */
export function storeTimezone(
  profile: Pick<UserProfile, 'tenant' | 'locations'> | null | undefined,
  locationId?: string | null,
): string {
  const own = locationId ? profile?.locations?.find((l) => l.id === locationId)?.timezone : null;
  if (own && isSupportedTimezone(own)) return own;
  const business = profile?.tenant?.timezone;
  return business && isSupportedTimezone(business) ? business : DEFAULT_TIMEZONE;
}

/** {@link storeTimezone} for the signed-in user. */
export function useStoreTimezone(locationId?: string | null): string {
  const profile = useAuthStore((s) => s.profile);
  return storeTimezone(profile, locationId);
}

/** YYYY-MM-DD now on `timeZone`'s clock. */
export function todayIn(timeZone: string, instant: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instant);
}

/** The hour (0–23) now on `timeZone`'s clock. */
export function hourIn(timeZone: string, instant: Date = new Date()): number {
  return parseInt(
    new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', hour12: false }).format(instant),
    10,
  ) % 24;
}

/** Shift a YYYY-MM-DD by N days — calendar arithmetic, no zone. */
export function shiftDate(date: string, deltaDays: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/** Days from `start` to `end` inclusive (YYYY-MM-DD). */
export function daysInclusive(start: string, end: string): number {
  return Math.round((Date.parse(`${end}T00:00:00Z`) - Date.parse(`${start}T00:00:00Z`)) / 86_400_000) + 1;
}

/** A picked calendar day — a date picker's local-midnight Date — as YYYY-MM-DD. */
export function calendarDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** YYYY-MM-DD as the local-midnight Date a date picker shows for that day. */
export function calendarDay(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * The range date picker's quick picks, anchored on the store's day (T20) — the
 * picker's own defaults count from this browser's day. Same labels as those.
 */
export function storeDatePresets(
  timeZone: string,
  now: Date = new Date(),
): { label: string; value: DateRange }[] {
  const today = calendarDay(todayIn(timeZone, now));
  const y = today.getFullYear();
  const m = today.getMonth();
  const day = (offset: number) => new Date(y, m, today.getDate() + offset);
  return [
    { label: 'Today', value: { from: today, to: today } },
    { label: 'Yesterday', value: { from: day(-1), to: day(-1) } },
    { label: 'Last 7 days', value: { from: day(-6), to: today } },
    { label: 'This month', value: { from: new Date(y, m, 1), to: today } },
    { label: 'Last month', value: { from: new Date(y, m - 1, 1), to: new Date(y, m, 0) } },
  ];
}
